<div class="space-y-6 pb-10">
  <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
    <div>
      <h2 class="text-2xl font-bold tracking-tight">Rider Wallet Manager</h2>
      <p class="text-base-content/60">
        View and adjust authorized rider wallets with a full audit trail.
      </p>
    </div>
    <button class="btn btn-outline" (click)="loadRiders(); loadWallet()" [disabled]="isLoading()">
      <i class="fa-solid fa-rotate"></i>
      Refresh
    </button>
  </div>

  <div class="grid grid-cols-1 gap-6 xl:grid-cols-[320px_1fr]">
    <div class="card border border-base-200 bg-base-100 shadow-sm">
      <div class="card-body gap-3 p-4">
        <select class="select select-bordered select-sm" [(ngModel)]="selectedStoreId" (ngModelChange)="resetRiderPage()" [disabled]="!auth.isSuperAdmin()">
          @if(auth.isSuperAdmin()){<option [ngValue]="0">All stores</option>}
          @for (store of stores(); track store.id) {
            <option [ngValue]="store.id">{{ store.name }}</option>
          }
        </select>

        <input
          class="input input-bordered input-sm"
          placeholder="Search rider name / code"
          [(ngModel)]="search"
          (ngModelChange)="resetRiderPage()"
        />

        <div class="max-h-[70vh] space-y-2 overflow-y-auto">
          @for (rider of paginatedRiders(); track rider.id) {
            <button
              type="button"
              class="w-full rounded-xl border border-base-200 p-3 text-left transition hover:bg-base-200/50"
              [class.border-primary]="selectedRiderId === rider.id"
              [class.bg-primary/5]="selectedRiderId === rider.id"
              (click)="selectRider(rider.id)"
            >
              <div class="font-bold">{{ rider.full_name || 'Rider' }}</div>
              <div class="text-xs opacity-50">{{ rider.code || rider.id }}</div>
              <div class="mt-1 text-xs font-semibold text-primary">
                ₹{{ (rider.wallet?.balance || 0) | number:'1.0-0' }}
              </div>
            </button>
          } @empty {
            <p class="py-8 text-center text-sm opacity-50">No riders found</p>
          }
        </div>
        <app-admin-paginator
          [totalItems]="filteredRiders().length"
          [currentPage]="riderPage"
          [pageSize]="riderPageSize"
          [showPageSize]="false"
          (pageChange)="riderPage = $event"
        />
      </div>
    </div>

    <div class="space-y-6">
      @if (!selectedRiderId) {
        <div class="card border border-dashed border-base-300 bg-base-100">
          <div class="card-body items-center py-20 text-center opacity-50">
            <i class="fa-solid fa-wallet text-4xl"></i>
            <p>Select a rider to view and customize their wallet</p>
          </div>
        </div>
      } @else if (isLoading()) {
        <div class="flex justify-center py-20">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      } @else if (details(); as data) {
        <div class="card border border-base-200 bg-base-100 shadow-sm">
          <div class="card-body">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 class="text-xl font-black">{{ data.rider.full_name || 'Rider' }}</h3>
                <p class="text-xs font-mono opacity-50">{{ data.rider.code || data.rider.id }}</p>
                <p class="text-xs opacity-60">{{ data.rider.phone || 'No phone' }} · {{ data.rider.status }}</p>
              </div>
            </div>

            <div class="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">Balance</p>
                <p class="text-lg font-black text-primary">₹{{ data.wallet.balance | number:'1.0-0' }}</p>
              </div>
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">Withdrawable</p>
                <p class="text-lg font-black">₹{{ data.wallet.withdrawable_balance | number:'1.0-0' }}</p>
              </div>
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">Lifetime</p>
                <p class="text-lg font-black">₹{{ data.wallet.lifetime_earning | number:'1.0-0' }}</p>
              </div>
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">Withdrawn</p>
                <p class="text-lg font-black">₹{{ data.wallet.total_payout_withdrawn | number:'1.0-0' }}</p>
              </div>
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">COD liability</p>
                <p class="text-lg font-black">₹{{ data.wallet.cod_liability | number:'1.0-0' }}</p>
              </div>
              <div class="rounded-xl bg-base-200/60 p-3">
                <p class="text-[10px] font-black uppercase opacity-50">Deliveries</p>
                <p class="text-lg font-black">{{ data.wallet.total_orders_completed }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div class="card border border-base-200 bg-base-100 shadow-sm">
            <div class="card-body">
              <h4 class="font-black uppercase tracking-widest text-sm opacity-60">Adjust wallet</h4>

              <div class="tabs tabs-boxed w-fit bg-base-200/50 p-1">
                <button class="tab" [class.tab-active]="action === 'credit'" (click)="action = 'credit'">Credit</button>
                <button class="tab" [class.tab-active]="action === 'debit'" (click)="action = 'debit'">Debit</button>
                <button class="tab" [class.tab-active]="action === 'set'" (click)="action = 'set'">Set balance</button>
              </div>

              <label class="form-control w-full">
                <div class="label"><span class="label-text font-semibold">Amount (₹)</span></div>
                <input class="input input-bordered" type="number" min="0" [(ngModel)]="amount" placeholder="e.g. 100">
              </label>

              <label class="form-control w-full">
                <div class="label"><span class="label-text font-semibold">Reason (required)</span></div>
                <textarea class="textarea textarea-bordered" rows="3" [(ngModel)]="reason" placeholder="e.g. Bonus for festival week"></textarea>
              </label>

              <label class="label cursor-pointer justify-start gap-3">
                <input type="checkbox" class="checkbox checkbox-sm" [(ngModel)]="adjustLifetime">
                <span class="label-text">Also add credit to lifetime earnings</span>
              </label>

              <label class="label cursor-pointer justify-start gap-3">
                <input type="checkbox" class="checkbox checkbox-sm" [(ngModel)]="setCodLiability">
                <span class="label-text">Update COD liability</span>
              </label>

              @if (setCodLiability) {
                <label class="form-control w-full">
                  <div class="label"><span class="label-text font-semibold">COD liability (₹)</span></div>
                  <input class="input input-bordered" type="number" min="0" [(ngModel)]="codLiability">
                </label>
              }

              <button class="btn btn-primary mt-2" (click)="submitAdjustment()" [disabled]="isSaving()">
                @if (isSaving()) {
                  <span class="loading loading-spinner loading-sm"></span>
                }
                Apply adjustment
              </button>
            </div>
          </div>

          <div class="card border border-base-200 bg-base-100 shadow-sm">
            <div class="card-body">
              <h4 class="font-black uppercase tracking-widest text-sm opacity-60">Recent transactions</h4>
              <div class="max-h-[420px] space-y-3 overflow-y-auto">
                @for (txn of data.recent_transactions; track txn.id) {
                  <div class="flex items-start justify-between gap-3 rounded-xl border border-base-200 p-3">
                    <div>
                      <p class="text-xs font-black uppercase">{{ txn.type }}</p>
                      <p class="mt-1 text-xs opacity-60">{{ txn.note }}</p>
                      <p class="mt-1 text-[10px] font-mono opacity-40">{{ txn.created_at | date:'medium' }}</p>
                    </div>
                    <p class="font-black" [class.text-success]="txn.amount > 0" [class.text-error]="txn.amount < 0">
                      {{ txn.amount > 0 ? '+' : '' }}₹{{ txn.amount | number:'1.0-0' }}
                    </p>
                  </div>
                } @empty {
                  <p class="py-10 text-center text-sm opacity-50">No wallet transactions yet</p>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</div>
